import { z } from 'zod';
import { uuid, isoDateTime } from './common.js';
/** Milestone 5: suppliers, purchase orders, transfers from remote storage, receipts, backorders, returns. */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
export const supplierDto = z.object({
    id: uuid,
    name: z.string(),
    contact: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    leadTimeDays: z.number().int().nullable(),
    currency: z.string(),
    note: z.string().nullable(),
    active: z.boolean(),
    openOrders: z.number().int(),
});
export const saveSupplierRequest = z.object({
    name: z.string().trim().min(1).max(200),
    contact: z.string().trim().max(200).nullable().optional(),
    email: z.string().trim().email().max(200).nullable().optional().or(z.literal('').transform(() => null)),
    phone: z.string().trim().max(50).nullable().optional(),
    leadTimeDays: z.number().int().min(0).max(365).nullable().optional(),
    currency: z.string().trim().length(3).default('DKK'),
    note: z.string().trim().max(2000).nullable().optional(),
    active: z.boolean().default(true),
});
export const purchaseOrderStatus = z.enum(['draft', 'sent', 'confirmed', 'partially_received', 'received', 'cancelled']);
export const purchaseOrderLineDto = z.object({
    id: uuid,
    variantId: uuid,
    sku: z.string(),
    title: z.string(),
    quantityOrdered: z.number().int(),
    quantityReceived: z.number().int(),
    unitCostMinor: z.number().int().nullable(),
    expectedAt: z.string().nullable(),
    note: z.string().nullable(),
    barcodes: z.array(z.string()),
});
export const purchaseOrderDto = z.object({
    id: uuid,
    number: z.string(),
    supplierId: uuid,
    supplierName: z.string(),
    warehouseId: uuid,
    warehouseCode: z.string(),
    status: purchaseOrderStatus,
    expectedAt: z.string().nullable(),
    reference: z.string().nullable(),
    note: z.string().nullable(),
    currency: z.string(),
    lineCount: z.number().int(),
    quantityOrdered: z.number().int(),
    quantityReceived: z.number().int(),
    totalCostMinor: z.number().int(),
    createdBy: z.string(),
    createdAt: isoDateTime,
    sentAt: isoDateTime.nullable(),
});
export const purchaseOrderDetailDto = purchaseOrderDto.extend({ lines: z.array(purchaseOrderLineDto), receipts: z.array(z.lazy(() => receiptDto)), supplierEmail: z.string().nullable() });
export const purchaseOrderLineInput = z.object({
    sku: z.string().trim().min(1).max(60),
    quantity: z.number().int().min(1).max(1_000_000),
    unitCostMinor: z.number().int().min(0).nullable().optional(),
    expectedAt: isoDate.nullable().optional(),
    note: z.string().trim().max(500).nullable().optional(),
});
export const savePurchaseOrderRequest = z.object({
    supplierId: uuid,
    warehouseId: uuid,
    expectedAt: isoDate.nullable().optional(),
    reference: z.string().trim().max(100).nullable().optional(),
    note: z.string().trim().max(2000).nullable().optional(),
    currency: z.string().trim().length(3).default('DKK'),
    lines: z.array(purchaseOrderLineInput).min(1).max(500),
});
export const purchaseOrderQuery = z.object({
    status: purchaseOrderStatus.optional(),
    open: z.coerce.boolean().optional(),
    supplierId: uuid.optional(),
    warehouseId: uuid.optional(),
    q: z.string().trim().max(100).optional(),
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(200).default(50),
});
export const confirmPurchaseOrderRequest = z.object({ reference: z.string().trim().max(100).nullable().optional(), expectedAt: isoDate.nullable().optional() });
export const sendPurchaseOrderRequest = z.object({ email: z.boolean().default(true), printerId: uuid.nullable().optional() });
/** Receiving: per line a quantity and the location it is put away to. */
export const receiveRequest = z.object({
    lines: z.array(z.object({ lineId: uuid, quantity: z.number().int().min(1), locationId: uuid })).min(1).max(500),
    note: z.string().trim().max(1000).nullable().optional(),
    /** Release backorders that the arrival covers (default on). */
    releaseBackorders: z.boolean().default(true),
});
export const transferStatus = z.enum(['requested', 'called', 'in_transit', 'received', 'cancelled']);
export const transferLineDto = z.object({
    id: uuid,
    variantId: uuid,
    sku: z.string(),
    title: z.string(),
    quantityRequested: z.number().int(),
    quantityShipped: z.number().int(),
    quantityReceived: z.number().int(),
    availableAtSource: z.number().int(),
    barcodes: z.array(z.string()),
});
export const transferDto = z.object({
    id: uuid,
    number: z.string(),
    fromWarehouseId: uuid,
    fromWarehouseCode: z.string(),
    toWarehouseId: uuid,
    toWarehouseCode: z.string(),
    status: transferStatus,
    requestedPallets: z.string().nullable(),
    expectedAt: z.string().nullable(),
    note: z.string().nullable(),
    lineCount: z.number().int(),
    quantityRequested: z.number().int(),
    createdBy: z.string(),
    createdAt: isoDateTime,
    shippedAt: isoDateTime.nullable(),
});
export const transferDetailDto = transferDto.extend({ lines: z.array(transferLineDto), receipts: z.array(z.lazy(() => receiptDto)) });
export const saveTransferRequest = z.object({
    fromWarehouseId: uuid,
    toWarehouseId: uuid,
    expectedAt: isoDate.nullable().optional(),
    requestedPallets: z.string().trim().max(200).nullable().optional(),
    note: z.string().trim().max(2000).nullable().optional(),
    lines: z.array(z.object({ sku: z.string().trim().min(1).max(60), quantity: z.number().int().min(1) })).min(1).max(500),
});
export const transferQuery = z.object({
    status: transferStatus.optional(),
    open: z.coerce.boolean().optional(),
    q: z.string().trim().max(100).optional(),
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(200).default(50),
});
export const shipTransferRequest = z.object({ lines: z.array(z.object({ lineId: uuid, quantity: z.number().int().min(0) })).optional() });
export const receiptDto = z.object({
    id: uuid,
    number: z.string(),
    sourceType: z.enum(['purchase_order', 'transfer', 'return']),
    sourceId: uuid.nullable(),
    sourceNumber: z.string().nullable(),
    warehouseCode: z.string(),
    receivedBy: z.string(),
    receivedAt: isoDateTime,
    note: z.string().nullable(),
    lines: z.array(z.object({ sku: z.string(), title: z.string(), quantity: z.number().int(), locationCode: z.string().nullable(), disposition: z.string().nullable() })),
});
/** What is on its way per variant: open purchase-order and transfer lines with the earliest date. */
export const incomingDto = z.object({ variantId: uuid, quantity: z.number().int(), expectedAt: z.string().nullable(), source: z.string() });
export const backorderDto = z.object({
    orderId: uuid,
    orderNumber: z.string(),
    orderStatus: z.string(),
    shopName: z.string().nullable(),
    placedAt: isoDateTime.nullable(),
    orderLineId: uuid,
    variantId: uuid.nullable(),
    sku: z.string(),
    title: z.string(),
    backorder: z.number().int(),
    available: z.number().int(),
    incoming: z.array(incomingDto),
});
export const backorderQuery = z.object({ q: z.string().trim().max(100).optional(), coverable: z.coerce.boolean().optional() });
/** Returns (RMA). */
export const returnStatus = z.enum(['open', 'labelled', 'received', 'closed', 'cancelled']);
export const returnDto = z.object({
    id: uuid,
    number: z.string(),
    orderId: uuid,
    orderNumber: z.string(),
    customerName: z.string().nullable(),
    warehouseCode: z.string(),
    status: returnStatus,
    reason: z.string().nullable(),
    note: z.string().nullable(),
    creditStatus: z.enum(['none', 'due', 'credited']),
    shipmentId: uuid.nullable(),
    labelDocumentId: uuid.nullable(),
    createdBy: z.string(),
    createdAt: isoDateTime,
    lines: z.array(z.object({ id: uuid, orderLineId: uuid, sku: z.string(), title: z.string(), quantity: z.number().int(), quantityReceived: z.number().int(), quantityRestocked: z.number().int(), quantityScrapped: z.number().int(), unitPriceMinor: z.number().int() })),
});
export const createReturnRequest = z.object({
    orderId: uuid,
    reason: z.string().trim().max(500).nullable().optional(),
    note: z.string().trim().max(2000).nullable().optional(),
    lines: z.array(z.object({ orderLineId: uuid, quantity: z.number().int().min(1) })).min(1),
    /** Book a GLS return label (ShopReturn) for the customer. */
    label: z.boolean().default(false),
});
export const receiveReturnRequest = z.object({
    lines: z.array(z.object({ lineId: uuid, restock: z.number().int().min(0), scrap: z.number().int().min(0), locationId: uuid.nullable().optional() })).min(1),
    note: z.string().trim().max(1000).nullable().optional(),
});
export const returnQuery = z.object({ status: returnStatus.optional(), orderId: uuid.optional(), q: z.string().trim().max(100).optional() });
//# sourceMappingURL=purchasing.js.map